import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Check, Save, Activity, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { ModeBadge } from "@/components/Brand";
import { toast } from "sonner";

function Readiness() {
  const [res, setRes] = useState(null);
  const [running, setRunning] = useState(false);
  const run = async () => {
    setRunning(true);
    try { const { data } = await api.get("/readiness"); setRes(data); }
    catch { toast.error("Readiness check failed"); }
    finally { setRunning(false); }
  };
  const Icon = ({ s }) => s === "pass" ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : s === "warn" ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <XCircle className="w-4 h-4 text-red-400" />;
  return (
    <div className="lf-card p-5" data-testid="readiness-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display font-semibold text-slate-100 flex items-center gap-2"><Activity className="w-4 h-4 text-primary" /> Demo Readiness</h3>
        <div className="flex items-center gap-2">
          {res && <ModeBadge mode={res.mode} testid="readiness-mode" />}
          <Button size="sm" onClick={run} disabled={running} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="run-readiness-btn">{running ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run check"}</Button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Verifies environment, database, live-provider connectivity and LLM access. Secrets are never displayed. To enable LIVE DATA, set <span className="font-mono text-slate-300">GEOAPIFY_API_KEY</span> in the backend environment (Geoapify is the primary provider).</p>
      {res && (
        <ul className="space-y-2" data-testid="readiness-results">
          {res.checks.map((c) => (
            <li key={c.name} className="flex items-start gap-2 text-sm" data-testid={`check-${c.name}`}>
              <Icon s={c.status} />
              <span className="font-mono text-xs text-slate-300 w-52 shrink-0 pt-0.5">{c.name}</span>
              <span className="text-xs text-slate-400">{c.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SERVICES = ["Website Development", "Website Redesign", "SEO", "Local SEO", "Google Business Profile Optimization", "Social Media Marketing", "Paid Advertising", "Branding", "Content Marketing", "WhatsApp Automation", "AI Chatbots", "CRM Automation", "Lead Automation", "Appointment Automation", "Custom Software", "AI Solutions"];
const INDUSTRIES = ["Salon", "Restaurant", "Gym", "Dentist", "Jewellery", "Real Estate", "Hotel", "Cafe", "Bakery", "Boutique", "Clinic", "Coaching Institute", "Interior Designer", "Automobile", "Local Services"];
const TONES = ["Professional", "Friendly", "Direct", "Consultative", "Premium", "Minimal"];

function Chip({ active, children, onClick, testid }) {
  return <button type="button" onClick={onClick} data-testid={testid} className={`px-3 py-1.5 rounded-lg text-sm border transition-all ${active ? "bg-primary/15 border-primary/50 text-primary" : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"}`}>{children}{active && <Check className="w-3 h-3 inline ml-1.5" />}</button>;
}
function Card({ title, children }) {
  return <div className="lf-card p-5"><h3 className="font-display font-semibold text-slate-100 mb-4">{title}</h3>{children}</div>;
}

export default function Settings() {
  const { profile, setProfile } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/profile").then(({ data }) => setF(data)); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/profile", { ...f, onboarded: true }); setProfile(data); toast.success("Settings saved — recommendations updated"); }
    catch { toast.error("Could not save."); }
    finally { setSaving(false); }
  };

  if (!f) return <div className="h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold text-slate-50">Settings</h1><p className="text-sm text-slate-400 mt-1">Changes affect all recommendations and generated outreach.</p></div>
        <Button onClick={save} disabled={saving} className="bg-primary hover:bg-emerald-600 text-white" data-testid="save-settings-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save changes</>}</Button>
      </div>

      <Card title="Agency">
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="text-slate-300 text-xs">Agency name</Label><Input value={f.agency_name || ""} onChange={(e) => set("agency_name", e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-agency" /></div>
            <div><Label className="text-slate-300 text-xs">Website</Label><Input value={f.website || ""} onChange={(e) => set("website", e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-website" /></div>
            <div><Label className="text-slate-300 text-xs">Email</Label><Input value={f.email || ""} onChange={(e) => set("email", e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-email" /></div>
            <div><Label className="text-slate-300 text-xs">WhatsApp</Label><Input value={f.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-whatsapp" /></div>
          </div>
          <div><Label className="text-slate-300 text-xs">Description</Label><Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-desc" /></div>
        </div>
      </Card>

      <Card title="Services you sell">
        <label className="flex items-center gap-2 mb-4 text-sm text-slate-300 cursor-pointer" data-testid="set-let-ai">
          <input type="checkbox" checked={f.let_ai_decide} onChange={(e) => set("let_ai_decide", e.target.checked)} className="accent-emerald-500" /> Let AI decide the best service per prospect
        </label>
        <div className="flex flex-wrap gap-2">{SERVICES.map((s) => <Chip key={s} active={(f.services || []).includes(s)} onClick={() => toggle("services", s)} testid={`set-service-${s}`}>{s}</Chip>)}</div>
      </Card>

      <Card title="Target market">
        <div className="grid sm:grid-cols-3 gap-4 mb-4">
          <div><Label className="text-slate-300 text-xs">City</Label><Input value={f.city || ""} onChange={(e) => set("city", e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-city" /></div>
          <div><Label className="text-slate-300 text-xs">PIN code</Label><Input value={f.pincode || ""} onChange={(e) => set("pincode", e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-pin" /></div>
          <div><Label className="text-slate-300 text-xs">Radius (km)</Label><Input type="number" value={f.radius_km || 0} onChange={(e) => set("radius_km", +e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-radius" /></div>
        </div>
        <Label className="text-slate-300 text-xs">Industries</Label>
        <div className="flex flex-wrap gap-2 mt-2">{INDUSTRIES.map((s) => <Chip key={s} active={(f.industries || []).includes(s)} onClick={() => toggle("industries", s)} testid={`set-industry-${s}`}>{s}</Chip>)}</div>
      </Card>

      <Card title="Pricing & tone">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div><Label className="text-slate-300 text-xs">Min deal (₹)</Label><Input type="number" value={f.deal_min || 0} onChange={(e) => set("deal_min", +e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-deal-min" /></div>
          <div><Label className="text-slate-300 text-xs">Max deal (₹)</Label><Input type="number" value={f.deal_max || 0} onChange={(e) => set("deal_max", +e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="set-deal-max" /></div>
        </div>
        <Label className="text-slate-300 text-xs">Outreach tone</Label>
        <div className="flex flex-wrap gap-2 mt-2">{TONES.map((t) => <Chip key={t} active={f.tone === t} onClick={() => set("tone", t)} testid={`set-tone-${t}`}>{t}</Chip>)}</div>
      </Card>

      <Readiness />
    </div>
  );
}
